import React, { useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { useToast } from "../hooks/use-toast";
import { Sparkles, TrendingUp, RotateCcw } from "lucide-react";
import { api, getVisitorId, rememberContact } from "../utils/api";
import { propertySearch } from "../mock";

const usd = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const HomeValueTool = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    beds: "",
    baths: "",
    sqft: "",
    condition: "",
    notes: "",
    subscribe: true,
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.city || !form.sqft) {
      toast({
        title: "A few details needed",
        description: "Please add your name, email, city, and square footage for an estimate.",
      });
      return;
    }
    setLoading(true);
    setEstimate(null);
    try {
      const visitorId = getVisitorId();
      rememberContact({ name: form.name, email: form.email });
      const { data } = await api.post("/home-value", {
        ...form,
        beds: form.beds ? Number(form.beds) : null,
        baths: form.baths ? Number(form.baths) : null,
        sqft: form.sqft ? Number(form.sqft) : null,
        visitorId,
      });
      setEstimate(data.estimate);
      toast({
        title: "Your estimate is ready!",
        description: "I've also saved your request — I'll reach out with a precise CMA.",
      });
    } catch (err) {
      toast({
        title: "Couldn't generate that estimate",
        description: "Please try again in a moment, or call me at 210-274-3556.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (estimate) {
    return (
      <div className="bg-[#141210] rounded-3xl p-8 md:p-11 border border-[#141210] text-[#e9dcc4]">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-gold" />
          <span className="text-xs tracking-luxury uppercase text-gold">AI Estimated Value</span>
        </div>
        <div className="font-display text-4xl md:text-6xl text-[#d4af37] mb-2">
          {usd(estimate.estimated_low)} – {usd(estimate.estimated_high)}
        </div>
        {estimate.price_per_sqft ? (
          <p className="text-[#a8967a] text-sm mb-6">
            ≈ {usd(estimate.price_per_sqft)} / sq ft in {form.city}, TX
          </p>
        ) : null}

        <p className="text-[#cabb9f] leading-relaxed mb-7 max-w-2xl">{estimate.summary}</p>

        {Array.isArray(estimate.factors) && estimate.factors.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {estimate.factors.map((f, i) => (
              <div key={`${i}-${String(f).slice(0, 16)}`} className="bg-[#0e0c0a] rounded-xl p-4 flex items-start gap-3">
                <TrendingUp className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                <span className="text-sm text-[#e9dcc4]">{f}</span>
              </div>
            ))}
          </div>
        )}

        <div className="rounded-2xl bg-[#0e0c0a] p-6 border border-[#2a241d]">
          <p className="text-sm text-[#cabb9f] leading-relaxed">
            This is a rough AI estimate — not an appraisal. For a precise, data-backed valuation of{" "}
            <span className="text-[#f4ebd9] font-medium">{form.address || "your home"}</span>, I'll
            prepare a full comparative market analysis (CMA) and reach out personally.
          </p>
        </div>

        <button
          onClick={() => setEstimate(null)}
          className="mt-7 inline-flex items-center gap-2 text-xs tracking-luxury uppercase text-gold hover:text-[#e3c98d] transition-colors"
        >
          <RotateCcw className="w-4 h-4" /> Estimate another home
        </button>

        <p className="text-[10px] text-[#8a7f6c] mt-6 leading-relaxed">
          Estimate generated by AI for informational purposes only. Actual market value varies —
          contact Kristy Russo for an accurate CMA.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label className="text-xs tracking-wide uppercase text-[#4a423a]">Your Name *</Label>
          <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Full name" className="bg-[#faf7f2] border-[#e0d6c5] focus-visible:ring-[#d4af37] h-11" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-wide uppercase text-[#4a423a]">Email *</Label>
          <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" className="bg-[#faf7f2] border-[#e0d6c5] focus-visible:ring-[#d4af37] h-11" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label className="text-xs tracking-wide uppercase text-[#4a423a]">Phone</Label>
          <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(210) 000-0000" className="bg-[#faf7f2] border-[#e0d6c5] focus-visible:ring-[#d4af37] h-11" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-wide uppercase text-[#4a423a]">Property Address</Label>
          <Input value={form.address} onChange={(e) => update("address", e.target.value)} placeholder="123 Main St" className="bg-[#faf7f2] border-[#e0d6c5] focus-visible:ring-[#d4af37] h-11" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        <div className="space-y-2 md:col-span-1">
          <Label className="text-xs tracking-wide uppercase text-[#4a423a]">City / Area *</Label>
          <Select value={form.city} onValueChange={(v) => update("city", v)}>
            <SelectTrigger className="bg-[#faf7f2] border-[#e0d6c5] focus:ring-[#d4af37] h-11">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {propertySearch.map((p) => (
                <SelectItem key={p.city} value={p.city}>{p.city}</SelectItem>
              ))}
              <SelectItem value="Other Texas Hill Country">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-wide uppercase text-[#4a423a]">Beds</Label>
          <Input type="number" value={form.beds} onChange={(e) => update("beds", e.target.value)} placeholder="3" className="bg-[#faf7f2] border-[#e0d6c5] focus-visible:ring-[#d4af37] h-11" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-wide uppercase text-[#4a423a]">Baths</Label>
          <Input type="number" step="0.5" value={form.baths} onChange={(e) => update("baths", e.target.value)} placeholder="2" className="bg-[#faf7f2] border-[#e0d6c5] focus-visible:ring-[#d4af37] h-11" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-wide uppercase text-[#4a423a]">Sq Ft *</Label>
          <Input type="number" value={form.sqft} onChange={(e) => update("sqft", e.target.value)} placeholder="2000" className="bg-[#faf7f2] border-[#e0d6c5] focus-visible:ring-[#d4af37] h-11" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="space-y-2">
          <Label className="text-xs tracking-wide uppercase text-[#4a423a]">Condition</Label>
          <Select value={form.condition} onValueChange={(v) => update("condition", v)}>
            <SelectTrigger className="bg-[#faf7f2] border-[#e0d6c5] focus:ring-[#d4af37] h-11">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Excellent / renovated">Excellent / renovated</SelectItem>
              <SelectItem value="Good">Good</SelectItem>
              <SelectItem value="Average">Average</SelectItem>
              <SelectItem value="Needs work">Needs work</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label className="text-xs tracking-wide uppercase text-[#4a423a]">Anything special? (upgrades, lot, view...)</Label>
          <Input value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Pool, new roof, corner lot, greenbelt..." className="bg-[#faf7f2] border-[#e0d6c5] focus-visible:ring-[#d4af37] h-11" />
        </div>
      </div>

      <div className="flex items-start gap-3">
        <Checkbox id="hv-sub" checked={form.subscribe} onCheckedChange={(v) => update("subscribe", !!v)} className="mt-0.5 data-[state=checked]:bg-[#d4af37] data-[state=checked]:border-[#d4af37]" />
        <Label htmlFor="hv-sub" className="text-sm text-[#6b6154] leading-relaxed font-normal">
          Email me market updates and my full CMA for this home.
        </Label>
      </div>

      <Button type="submit" disabled={loading} className="w-full md:w-auto bg-[#d4af37] hover:bg-[#c19a2e] text-[#141210] rounded-full px-10 h-12 tracking-luxury uppercase text-xs font-semibold">
        <Sparkles className="w-4 h-4 mr-2" /> {loading ? "Calculating..." : "Get My Estimate"}
      </Button>
      <p className="text-[11px] text-[#9a8f7d]">
        AI-powered estimate for informational purposes — not an appraisal. Kristy will follow up with a precise CMA.
      </p>
    </form>
  );
};

export default HomeValueTool;
